// Coerenza fra un codice fiscale e i dati anagrafici digitati nel modulo di iscrizione.
//
// È la stessa regola che applica il backend in models/CodiceFiscale.cs, ripetuta qui per dirlo
// all'utente mentre compila invece che dopo l'invio. L'autorità resta il server: questo controllo
// evita il viaggio, non lo sostituisce.
//
// Delle sedici posizioni se ne ricalcolano dodici. Le quattro del comune di nascita
// richiederebbero la tabella dei codici catastali e un campo che il modulo non chiede, quindi
// restano fuori dal confronto.

export const CODICE_PASSEPARTOUT = '1234';

// Sotto questa soglia un codice censimento non identifica nessuno: il backend lo scarta in
// scrittura, quindi tanto vale dirlo qui. Deve restare allineata a GiocatoreIdentita.CodCifreMinime.
export const CODICE_CIFRE_MINIME = 5;

const LUNGHEZZA = 16;
const VOCALI = 'AEIOU';
const MESI = 'ABCDEHLMPRST';
const SCARTO_FEMMINA = 40;

// Cifra sostituita dalla lettera corrispondente, per indice: è l'omocodia, cioè quello che
// l'Agenzia delle Entrate fa quando due persone otterrebbero lo stesso codice.
const OMOCODIA = 'LMNPQRSTUV';

// Posizioni che nel codice sono cifre, e che l'omocodia può quindi sostituire.
const POSIZIONI_NUMERICHE = [6, 7, 9, 10, 12, 13, 14];

const DISPARI = {
  0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21,
  A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21,
  K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14,
  U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23
};

const MESSAGGI = {
  formato: 'Il codice fiscale non ha un formato valido (es. RSSMRA80A01H501U)',
  controllo: 'Il codice fiscale contiene un errore di battitura (il carattere di controllo non torna)',
  cognome: 'Il codice fiscale non corrisponde al cognome indicato',
  nome: 'Il codice fiscale non corrisponde al nome indicato',
  data: 'Il codice fiscale non corrisponde alla data di nascita indicata',
  sesso: 'Il codice fiscale non corrisponde al sesso indicato'
};

/** Il valore ridotto alle sole lettere A-Z: via accenti, apostrofi e spazi. */
const soloLettere = (valore) =>
  (valore || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

const sigla = (lettere, regolaDelNome) => {
  const consonanti = [...lettere].filter((c) => !VOCALI.includes(c)).join('');
  const vocali = [...lettere].filter((c) => VOCALI.includes(c)).join('');

  if (regolaDelNome && consonanti.length >= 4) {
    return consonanti[0] + consonanti[2] + consonanti[3];
  }
  return (consonanti + vocali + 'XXX').slice(0, 3);
};

export const siglaCognome = (cognome) => sigla(soloLettere(cognome), false);

/**
 * Le tre lettere del nome. Con quattro o più consonanti si prendono la prima, la terza e la
 * quarta, quindi Francesco diventa FNC e non FRN.
 */
export const siglaNome = (nome) => sigla(soloLettere(nome), true);

export const carattereControllo = (primiQuindici) => {
  let somma = 0;
  for (let i = 0; i < primiQuindici.length; i += 1) {
    const c = primiQuindici[i];
    if (i % 2 === 0) {
      // le posizioni si contano da uno: gli indici pari sono le posizioni dispari
      somma += DISPARI[c] ?? 0;
    } else {
      somma += /[0-9]/.test(c) ? Number(c) : c.charCodeAt(0) - 65;
    }
  }
  return String.fromCharCode(65 + (somma % 26));
};

/** Il codice ripulito e in maiuscolo, oppure null se non ha la forma di un codice fiscale. */
export const normalizzaCodiceFiscale = (cf) => {
  const c = (cf || '').trim().toUpperCase();
  if (c.length !== LUNGHEZZA) return null;

  for (let i = 0; i < LUNGHEZZA; i += 1) {
    const numerica = POSIZIONI_NUMERICHE.includes(i);
    if (numerica) {
      if (!/[0-9]/.test(c[i]) && !OMOCODIA.includes(c[i])) return null;
    } else if (!/[A-Z]/.test(c[i])) {
      return null;
    }
  }
  return c;
};

/** Riporta a cifre le posizioni che l'omocodia ha sostituito con lettere. */
const senzaOmocodia = (codice) => {
  const c = [...codice];
  POSIZIONI_NUMERICHE.forEach((i) => {
    const cifra = OMOCODIA.indexOf(c[i]);
    if (cifra >= 0) c[i] = String(cifra);
  });
  return c.join('');
};

/**
 * Confronta il codice con i dati anagrafici e restituisce il messaggio di quello che non torna,
 * oppure null se il codice è coerente. Restituisce null anche quando i dati non bastano ancora a
 * decidere: mentre si compila il modulo mancano quasi sempre, e segnalare un errore su un campo
 * che l'utente non ha ancora riempito sarebbe solo rumore.
 */
export const verificaCodiceFiscale = (cf, { nome, cognome, dataNascita, sesso }) => {
  const codice = normalizzaCodiceFiscale(cf);
  if (!codice) return MESSAGGI.formato;

  const sessoNormalizzato = (sesso || '').trim().toUpperCase();
  if (!soloLettere(nome) || !soloLettere(cognome) || !dataNascita ||
      (sessoNormalizzato !== 'M' && sessoNormalizzato !== 'F')) {
    return null;
  }

  const nascita = new Date(dataNascita);
  if (Number.isNaN(nascita.getTime())) return null;

  if (carattereControllo(codice.slice(0, 15)) !== codice[15]) return MESSAGGI.controllo;
  if (codice.slice(0, 3) !== siglaCognome(cognome)) return MESSAGGI.cognome;
  if (codice.slice(3, 6) !== siglaNome(nome)) return MESSAGGI.nome;

  const cifre = senzaOmocodia(codice);
  const anno = String(nascita.getFullYear() % 100).padStart(2, '0');
  if (cifre.slice(6, 8) !== anno) return MESSAGGI.data;
  if (cifre[8] !== MESI[nascita.getMonth()]) return MESSAGGI.data;

  const giornoCodice = Number(cifre.slice(9, 11));
  const femminaCodice = giornoCodice > SCARTO_FEMMINA;
  const giorno = femminaCodice ? giornoCodice - SCARTO_FEMMINA : giornoCodice;
  if (giorno !== nascita.getDate()) return MESSAGGI.data;

  return femminaCodice === (sessoNormalizzato === 'F') ? null : MESSAGGI.sesso;
};
